import { Box, Button, Heading, HStack, Icon, useAnalytics, VStack, Alert } from '@medely/ui-kit';
import { FixedFooterLayout } from '../../../../../../components/FixedFooterLayout';
import React, { useEffect } from 'react';
import { ExperienceSummaryCard } from './ExperienceSummaryCard';
import { useExperienceSummaryViewQueries } from './hooks';
import useDialog from '../../../../../../hooks/useDialog';
import { AddExperienceModal } from '../workExperienceModal/AddExperienceModal';
import { ExperienceSummaryResumeModal } from './ExperienceSummaryResumeModal';
import { useCurrentUser } from '@mp/hooks';
import { useExperienceValidationFromContext } from '../../validation/useExperienceValidationFromContext';
import { useApplicationMutations } from '@mp/pages/application/applicationFlowMutations';
import { useRegisterApplicationBackAction } from '@mp/pages/application/applicationBackActionStore';

export const ExperienceSummaryView = ({
  isMobileVersion = false,
}: {
  isMobileVersion?: boolean;
}) => {
  const analytics = useAnalytics();
  const { proType } = useCurrentUser();
  const { closeDialog, open, openDialog } = useDialog();
  const resumeDialog = useDialog();
  const {
    currentUser,
    workHistories,
    isLoading: queryIsLoading,
  } = useExperienceSummaryViewQueries();

  const { canContinue, message } = useExperienceValidationFromContext();

  const professionalPositions = currentUser?.professional?.professional_positions || [];

  const { nextStep, prevStep, isLoading: mutationIsLoading } = useApplicationMutations();

  const disableButtons = queryIsLoading || mutationIsLoading;

  const handleBackClick = () => {
    prevStep();
  };

  const handleContinueClick = () => {
    nextStep();
  };

  useRegisterApplicationBackAction(isMobileVersion ? handleBackClick : undefined, disableButtons);

  useEffect(() => {
    analytics.track('Product Application Experience Summary  - Viewed', {
      account_id: currentUser?.id,
      context: 'Product Application Experience Summary',
      subcontext: null,
      action: 'Viewed',
      pro_type: proType,
    });
  }, []);

  return (
    <FixedFooterLayout
      footerContent={
        isMobileVersion ? (
          <Box>
            <Button
              onClick={handleContinueClick}
              variant="contained"
              fullWidth
              disabled={disableButtons || !canContinue}
            >
              Continue
            </Button>
          </Box>
        ) : (
          <HStack gap={2}>
            <Box width="48%">
              <Button
                onClick={handleBackClick}
                variant="outlined"
                fullWidth
                disabled={disableButtons}
              >
                Back
              </Button>
            </Box>
            <Box width="48%">
              <Button
                onClick={handleContinueClick}
                variant="contained"
                fullWidth
                disabled={disableButtons || !canContinue}
              >
                Continue
              </Button>
            </Box>
          </HStack>
        )
      }
      title={
        <Heading size="s" testId="experience-summary-title">
          Complete your clinical experience to maximize earnings (optional)
        </Heading>
      }
      height="95%"
    >
      <VStack py={2} px={2} gap={2}>
        {professionalPositions?.map((professionalPosition) => (
          <ExperienceSummaryCard
            key={professionalPosition.position.id}
            position={professionalPosition.position}
            workHistories={workHistories?.filter(
              (workHistory) => workHistory.position_id === professionalPosition.position.id,
            )}
          />
        ))}
        <Button
          variant="text"
          fullWidth
          startIcon={
            <Icon name="plus" iconSize="18px" variant="custom" color="state.teal.primary" />
          }
          onClick={() => {
            openDialog();
          }}
          testId="experience-summary-add-experience-button"
        >
          Add experience
        </Button>
        <Button
          variant="text"
          fullWidth
          startIcon={
            <Icon name="plus" iconSize="18px" variant="custom" color="state.teal.primary" />
          }
          onClick={() => resumeDialog.openDialog()}
        >
          Upload resume
        </Button>
        {!canContinue && (
          <Alert severity="error" description={message} hideCloseIcon showIcon={false} />
        )}
      </VStack>
      <AddExperienceModal open={open} onClose={closeDialog} />
      <ExperienceSummaryResumeModal open={resumeDialog.open} onClose={resumeDialog.closeDialog} />
    </FixedFooterLayout>
  );
};
